import { useState, useRef, useEffect } from 'react';
import { getSkillEmoji, getSkillDescription } from '../utils/battleUtils';
import { MaterialAward, DiscoveredRecipe } from '../types/battle';

type LogFilter = 'all' | 'combat' | 'movement' | 'important';

export function useBattleLog() {
  const [battleLog, setBattleLog] = useState<string[]>([]);
  const [battleLogFilter, setBattleLogFilter] = useState<LogFilter>('all');
  const logContentRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new log entries are added
  useEffect(() => {
    if (logContentRef.current) {
      logContentRef.current.scrollTop = logContentRef.current.scrollHeight;
    }
  }, [battleLog]);

  const addToBattleLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setBattleLog(prev => {
      const lastMessage = prev[prev.length - 1];
      const newLogEntry = `[${timestamp}] ${message}`;

      if (message.includes('- The battle begins!')) {
        const exactBattleStartExists = prev.some(entry => entry.includes(message));
        if (exactBattleStartExists) return prev;
      }

      if (message.includes('⚠️ DEBUG:') || message.includes('End turn response received') ||
          message.includes('Turn advanced to:') || (message.includes('Processing') && message.includes('AI actions')) ||
          message.includes('AI Action Structure:') || message.includes('Unknown AI action type:')) {
        return prev;
      }

      if (lastMessage && lastMessage.includes(message)) return prev;

      return [...prev, newLogEntry];
    });
  };

  const addEnhancedBattleLog = (type: string, data: Record<string, unknown>) => {
    const timestamp = new Date().toLocaleTimeString();
    const s = (key: string) => String(data[key] ?? '');
    const n = (key: string) => Number(data[key] ?? 0);
    let message = '';

    if (type === 'debug') return;

    switch (type) {
      case 'battle_start':
        message = `🏛️ **${s('battleName')}** - The battle begins!`;
        break;

      case 'unit_move': {
        const dist = n('distance');
        if (dist > 3) {
          message = `🏃 ${s('unitName')} charges across the battlefield`;
        } else if (dist > 1) {
          message = `👣 ${s('unitName')} advances forward`;
        } else {
          message = `🔄 ${s('unitName')} repositions carefully`;
        }
        break;
      }

      case 'attack': {
        const remainHP = n('targetRemainingHP');
        const maxHP = n('targetMaxHP');
        const healthPercent = maxHP > 0 ? remainHP / maxHP : 0;
        let healthStatus = '';

        if (data.targetDefeated) {
          healthStatus = ' - **💀 DEFEATED!**';
        } else if (healthPercent < 0.25) {
          healthStatus = ' - *critically wounded*';
        } else if (healthPercent < 0.5) {
          healthStatus = ' - *badly wounded*';
        } else if (healthPercent < 0.75) {
          healthStatus = ' - *wounded*';
        }

        const dmg = n('damage');
        const avgDamage = maxHP * 0.15;
        let attackDesc = '⚔️';
        if (dmg > avgDamage * 1.5) {
          attackDesc = '💥 **CRITICAL HIT!**';
        } else if (dmg > avgDamage * 1.2) {
          attackDesc = '🗡️ *Powerful strike!*';
        }

        message = `${attackDesc} ${s('attackerName')} strikes ${s('targetName')} for ${dmg} damage${healthStatus}`;

        const comboDescription = s('comboDescription');
        if (comboDescription) {
          message = `${comboDescription}\n${message}`;
        }
        break;
      }

      case 'skill_cast': {
        const skillEmoji = getSkillEmoji(s('skillName'));
        const affectedCount = n('affectedCount');
        const skillDesc = getSkillDescription(s('skillName'), affectedCount);
        const dmg = n('damage');
        const heal = n('healing');
        const targetName = s('targetName');
        const targetSuffix = targetName ? ` to ${targetName}` : '';

        if (dmg && affectedCount > 1) {
          message = `${skillEmoji} ${s('casterName')} ${skillDesc} hitting ${affectedCount} enemies for ${dmg} damage!`;
        } else if (dmg) {
          message = `${skillEmoji} ${s('casterName')} ${skillDesc}${targetSuffix} for ${dmg} damage!`;
        } else if (heal) {
          message = `💚 ${s('casterName')} ${skillDesc}${targetSuffix} restoring ${heal} health!`;
        } else {
          message = `${skillEmoji} ${s('casterName')} ${skillDesc}${targetSuffix}`;
        }
        break;
      }

      case 'turn_start':
        if (data.isPlayerUnit) {
          message = `🎯 **${s('unitName')}'s turn**`;
        } else {
          message = `👹 ${s('unitName')}'s turn`;
        }
        break;

      case 'victory':
        message = `🏆 **VICTORY!** All enemies have been vanquished!`;
        break;

      case 'defeat':
        message = `💀 **DEFEAT!** Your party has fallen in battle.`;
        break;

      case 'materials_found':
        if (Array.isArray(data.materials) && data.materials.length > 0) {
          message = `💎 **Loot discovered:** ${(data.materials as MaterialAward[]).map((m) => `${m.quantity}x ${m.materialName}`).join(', ')}`;
        }
        break;

      case 'recipes_discovered':
        if (Array.isArray(data.recipes) && data.recipes.length > 0) {
          message = `📜 **New recipes learned:** ${(data.recipes as DiscoveredRecipe[]).map((r) => r.recipeName).join(', ')}`;
        }
        break;

      case 'xp_gain':
        message = `📈 ${s('unitName')} gains ${n('xpAmount')} XP!`;
        break;

      case 'error':
        message = s('message');
        break;

      case 'passive_ability':
        message = `🔮 ${s('unitName')}'s ${s('abilityName')}: ${s('description')}`;
        break;

      default:
        message = s('message');
    }

    if (message) {
      setBattleLog(prev => {
        const lastMessage = prev[prev.length - 1];
        const newLogEntry = `[${timestamp}] ${message}`;

        if (message.includes('- The battle begins!')) {
          const battleStartExists = prev.some(entry => entry.includes('- The battle begins!'));
          if (battleStartExists) return prev;
        }

        if (lastMessage && lastMessage.includes(message)) return prev;

        return [...prev, newLogEntry];
      });
    }
  };

  return {
    battleLog,
    setBattleLog,
    battleLogFilter,
    setBattleLogFilter,
    logContentRef,
    addToBattleLog,
    addEnhancedBattleLog,
  };
}
